import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { motion } from "framer-motion";
import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { memo, useState } from "react";

import { useAccess, useAuditedCourses, useUserAction } from "~/hooks";
import useFreeCourse from "~/hooks/useFreeCourse";
import useTrackCTA from "~/hooks/useTrackCTA";
import { buttonVariants } from "~/lib/animations.lib";

interface CourseSectionsControlsProps {
	expandedSections: string[];
	setExpandedSections: (expandedSections: string[]) => void;
	allSectionKeys: string[];
	hasCourseAccess: boolean;
	courseId: string;
}

const ANIMATION_DURATION = 120;

const CourseSectionsControls = ({
	expandedSections,
	setExpandedSections,
	allSectionKeys,
	hasCourseAccess,
	courseId,
}: CourseSectionsControlsProps) => {
	const { openClaimIfOffered } = useFreeCourse(courseId);
	const { isLoadingAccess } = useAccess();
	const { userId, isLoaded } = useAuth();
	const { setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();
	const [isExpanding, setIsExpanding] = useState(false);
	const [isCollapsing, setIsCollapsing] = useState(false);

	const { isLoadingAuditedCourses } = useAuditedCourses();

	const isLoading = !isLoaded || isLoadingAccess || isLoadingAuditedCourses;

	const handleExpandAll = async () => {
		if (!userId) {
			trackCTA(UserAction.SIGN_UP, "Course Expand All Sections");
			setUserAction(UserAction.SIGN_UP);
			return;
		}

		if (!hasCourseAccess) {
			if (openClaimIfOffered()) return;

			trackCTA(UserAction.BECOME_A_MEMBER, "Course Expand All Sections");
			setUserAction(UserAction.BECOME_A_MEMBER);
			return;
		}

		setIsExpanding(true);

		requestAnimationFrame(() => {
			setExpandedSections(allSectionKeys);
			setTimeout(() => setIsExpanding(false), ANIMATION_DURATION);
		});
	};

	const handleCollapseAll = async () => {
		if (!userId || !hasCourseAccess) {
			trackCTA(UserAction.SIGN_UP, "Course Collapse All Sections");
			setUserAction(UserAction.SIGN_UP);
			return;
		}

		setIsCollapsing(true);

		requestAnimationFrame(() => {
			setExpandedSections([]);
			window.scrollTo({ top: 0, behavior: "instant" });
			setTimeout(() => setIsCollapsing(false), ANIMATION_DURATION);
		});
	};

	return (
		<div className="phablet:flex hidden w-full phablet:max-w-max phablet:justify-start gap-3 phablet:pr-6">
			{expandedSections.length !== allSectionKeys.length && (
				<motion.button
					variants={buttonVariants}
					initial="idle"
					animate={isExpanding ? "loading" : "idle"}
					whileTap="tap"
					disabled={isLoading}
					id="course-expand-all-btn"
					onClick={handleExpandAll}
					className="flex items-center gap-0.5 rounded-full bg-slate-700 px-3 py-1.5 font-medium text-white shadow-black/5 shadow-xl hover:bg-slate-600 disabled:opacity-70 dark:bg-slate-600 dark:shadow-black/10 dark:hover:bg-slate-700"
				>
					Expand All
					<ChevronsUpDown className="-mr-1 h-5 w-5" strokeWidth={2.3} />
				</motion.button>
			)}

			{!!expandedSections.length && (
				<motion.button
					variants={buttonVariants}
					initial="idle"
					animate={isCollapsing ? "loading" : "idle"}
					whileTap="tap"
					disabled={isLoading}
					onClick={handleCollapseAll}
					className="flex items-center gap-0.5 rounded-full bg-slate-700 px-3 py-1.5 font-medium text-white shadow-black/5 shadow-xl hover:bg-slate-600 disabled:opacity-70 dark:bg-slate-600 dark:shadow-black/10 dark:hover:bg-slate-700"
				>
					Collapse All
					<ChevronsDownUp className="-mr-1 h-5 w-5" strokeWidth={2.3} />
				</motion.button>
			)}
		</div>
	);
};

export default memo(CourseSectionsControls);
